// Tells whether the recorded screen is changing, so Auto-Pace can see work done in another app
// (typing in Colab, scrolling, code printing output) that StudyBoard's own key/mouse listeners
// cannot. Runs entirely in the browser: a small grey copy of each frame is compared with the
// frames of the last moment. No network, no AI.

const W = 480, H = 270;          // big enough that one typed character changes a few pixels
const PIXEL_DELTA = 40;          // grey-level difference that counts as "this pixel changed"
const MIN_CHANGED = 4;           // changed pixels needed to call it activity
const HISTORY_MS = 1600;         // a text caret blinks about once a second; it matches a frame in here

export interface ScreenActivityProbe {
    /** Resolves true if the screen changed since the last moment; null when no frame was ready. */
    sample: () => Promise<boolean | null>;
    /** Forget earlier frames (call when StudyBoard is back in front). */
    reset: () => void;
    stop: () => void;
}

export function createScreenActivityProbe(track: MediaStreamTrack): ScreenActivityProbe | null {
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;

    // ImageCapture keeps working while this tab is hidden; a <video> is the fallback.
    const IC = (window as any).ImageCapture;
    const capture = IC ? new IC(track) : null;
    let video: HTMLVideoElement | null = null;
    if (!capture) {
        video = document.createElement('video');
        video.muted = true; video.playsInline = true;
        video.srcObject = new MediaStream([track]);
        video.play().catch(() => {});
    }

    let history: { t: number; g: Uint8Array }[] = [];
    let busy = false;
    let stopped = false;

    const grab = async (): Promise<boolean> => {
        if (capture) {
            // Chrome only sends a screen frame when something changed, so on a still screen this waits.
            const bmp: ImageBitmap = await capture.grabFrame();
            ctx.drawImage(bmp, 0, 0, W, H);
            bmp.close?.();
            return true;
        }
        if (!video || video.readyState < 2) return false;
        ctx.drawImage(video, 0, 0, W, H);
        return true;
    };

    const sample = async (): Promise<boolean | null> => {
        if (busy || stopped || track.readyState !== 'live') return null;
        busy = true;
        try {
            if (!(await grab()) || stopped) return null;
            const px = ctx.getImageData(0, 0, W, H).data;
            const g = new Uint8Array(W * H);
            for (let i = 0, j = 0; j < g.length; i += 4, j++) g[j] = (px[i] * 77 + px[i + 1] * 150 + px[i + 2] * 29) >> 8;

            const now = performance.now();
            let changed = 0;
            if (history.length) {
                // A pixel counts only if it differs from every recent frame, so a blinking caret
                // (which flips between two looks) never reads as work.
                for (let j = 0; j < g.length && changed < MIN_CHANGED; j++) {
                    const v = g[j];
                    let fresh = true;
                    for (let k = 0; k < history.length; k++) {
                        if (Math.abs(v - history[k].g[j]) <= PIXEL_DELTA) { fresh = false; break; }
                    }
                    if (fresh) changed++;
                }
            }
            history.push({ t: now, g });
            history = history.filter((h, k) => k === history.length - 1 || now - h.t < HISTORY_MS);
            return changed >= MIN_CHANGED;
        } catch {
            return null;
        } finally {
            busy = false;
        }
    };

    return {
        sample,
        reset: () => { history = []; },
        stop: () => {
            stopped = true; history = [];
            if (video) { video.pause(); video.srcObject = null; video = null; }
        },
    };
}
